import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";
import Header from "../../components/header/Header";
// @ts-ignore vendored plain-JS engine (gnovotny/nothing-to-watch)
import voroforce from "../../vendor/voroforce";
import config, { MEDIA_BASE_URL, SUBGRID_SIZE } from "./vf/config";

const JSON_BASE_URL = process.env.REACT_APP_VORO_JSON_URL || "/voro/json";
const POSTER_BASE_URL = "https://image.tmdb.org/t/p/w185";

type Movie = {
  id: number;
  title: string;
  poster_path?: string;
  release_date?: string;
  vote_average?: number;
};

type Cell = { subgrid: number; subgridIndex: number };

const batches = new Map<number, Movie[]>();

const getCellMovie = async (cell: Cell): Promise<Movie | undefined> => {
  let batch = batches.get(cell.subgrid);
  if (!batch) {
    try {
      const res = await fetch(`${JSON_BASE_URL}/${cell.subgrid}.json`);
      batch = res.ok ? await res.json() : [];
    } catch {
      batch = [];
    }
    batches.set(cell.subgrid, batch as Movie[]);
  }
  return batch?.[cell.subgridIndex % SUBGRID_SIZE];
};

const ExplorePage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [movie, setMovie] = useState<Movie>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Needs WebGL2, and needs the generated atlases to be reachable (see README).
    // Either can be missing in a deployed environment, so fail visibly instead of black.
    let instance: any;
    try {
      instance = voroforce(container, config);
    } catch (e) {
      console.error("voroforce init failed", e);
      setFailed(true);
      return;
    }

    void fetch(`${MEDIA_BASE_URL}/low/dds/0.dds`, { method: "HEAD" })
      .then((res) => setFailed(!res.ok))
      .catch(() => setFailed(true));

    instance.controls.listen("focused", ({ cell }: { cell?: Cell }) => {
      if (cell) void getCellMovie(cell).then(setMovie);
    });
    instance.controls.listen("selected", ({ cell }: { cell?: Cell }) => {
      if (!cell) return;
      void getCellMovie(cell).then((selected) => {
        if (selected) navigate(`${ROUTES.DETAILS}/${selected.id}`);
      });
    });

    return () => {
      // ponytail: engine dispose() touches helpers that lack a dispose method upstream;
      // swallow so a failed teardown can't blank the app on route change.
      try {
        instance.dispose();
      } catch (e) {
        console.warn("voroforce dispose failed", e);
      }
      container.replaceChildren();
    };
  }, [navigate]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      <Header />
      {failed && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            opacity: 0.6,
            textAlign: "center",
            padding: 24,
          }}
        >
          Gallery unavailable — needs WebGL2 and the generated poster atlases.
        </div>
      )}
      {movie && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 24,
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            color: "#fff",
            pointerEvents: "none",
            textShadow: "0 1px 4px rgba(0,0,0,.8)",
          }}
        >
          {movie.poster_path && (
            <img src={`${POSTER_BASE_URL}${movie.poster_path}`} alt="" width={60} style={{ borderRadius: 4 }} />
          )}
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{movie.title}</div>
            <div style={{ opacity: 0.7, fontSize: 14 }}>
              {movie.release_date?.slice(0, 4)}
              {movie.vote_average ? ` · ${movie.vote_average.toFixed(1)}` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
